import React, {useCallback} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';
import {NoteBanner} from 'src/modules/sso-settings/components/get-started/components/note-banner';

import {CheckBox} from '@pb/kit-component-checkbox';
import Group from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {useSsoSessionStorage} from '../../hooks/use-sso-session-storage';
import {StepFooter} from '../step-footer/step-footer';

import {AttributeConfigInstructions} from './components/attribute-config-instructions';
import {FieldMappingRow} from './components/field-mapping-row';
import {ATTRIBUTE_NAMES_CASE_SENSITIVE_MESSAGE} from './utils/attribute-instructions-data';

import {confirmLoginFieldsContainer, confirmationRow, fieldMappingCard} from './confirm-login-fields.module.styl';

type ConfirmLoginFieldsProps = {
    onContinue: () => void,
    onBack: () => void,
    saving?: boolean,
};

export const ConfirmLoginFields: React.FC<ConfirmLoginFieldsProps> = ({onContinue, onBack, saving}) => {
    const [confirmed, setConfirmed] = useSsoSessionStorage<boolean>('confirmLoginFields', false);
    const [selectedProvider] = useSsoSessionStorage<string | null>('selectedProvider', null);
    const {attributeCopy, ssoConfirmCheck} = useEventTracking();

    const handleCheckboxChange = useCallback((payload: unknown) => {
        let checked: boolean;
        if (typeof payload === 'boolean') {
            checked = payload;
        } else {
            checked = Boolean((payload as {event?: {target?: {checked?: boolean}}})?.event?.target?.checked);
        }
        if (checked) {
            ssoConfirmCheck();
        }
        setConfirmed(checked);
    }, [setConfirmed, ssoConfirmCheck]);

    return (
        <Group direction="v" gap="l" className={confirmLoginFieldsContainer}>
            <Group direction="v" gap="xs">
                <Typography variant="heading-level-1">
                    Provide Required User Attributes
                </Typography>
                <Typography variant="body-level-1">
                    Your Identity Provider must send the following user attributes exactly as listed below.
                    These values are required for SSO to work correctly and cannot be changed.
                </Typography>
            </Group>

            <NoteBanner message={ATTRIBUTE_NAMES_CASE_SENSITIVE_MESSAGE}/>

            <AttributeConfigInstructions providerId={selectedProvider}/>

            <div className={fieldMappingCard}>
                <Typography variant="heading-level-3">
                    Required Attributes
                </Typography>

                <FieldMappingRow
                    label="Email Address"
                    value="email"
                    helperText="This must match the email field your company login sends."
                    onCopy={() => attributeCopy({attribute: 'email'})}
                />
                <FieldMappingRow
                    label="First Name"
                    value="firstName"
                    onCopy={() => attributeCopy({attribute: 'firstName'})}
                />
                <FieldMappingRow
                    label="Last Name"
                    value="lastName"
                    onCopy={() => attributeCopy({attribute: 'lastName'})}
                />
            </div>

            <Group alignItems="center" gap="s" className={confirmationRow}>
                <CheckBox
                    checked={confirmed}
                    onCheckBoxChange={handleCheckboxChange}
                    usePropsOnly={true}
                />
                <Typography variant="body-level-1">
                    I have configured these attribute mappings in my Identity Provider.
                </Typography>
            </Group>

            <StepFooter
                onContinue={onContinue}
                onBack={onBack}
                continueDisabled={!confirmed}
                busy={saving}
            />
        </Group>
    );
};
